import { joinPaths } from 'Lib/Object/Path/shared';
import React from 'react';
import { InjectedPropsModifier } from '..';
import { NamePrefixConsumerProps } from './types';

type NamePrefixModifierProps = {
  /**
   * A path to use as the name prefix
   */
  namePrefix?: string;
  /**
   * A path to be appended to the current name prefix
   */
  appendNamePrefix?: string;
};

/**
 * This modifier can be used to set to hierarchically modify the prefix to be applied to the name property of child components.
 *
 * It provides both the prefix and a function that applies this prefix to a given string.
 *
 *  @example
 * ```
 * <element />                      // element has no access to namePrefix or prefixName
 *
 * <NamePrefixModifier namePrefix="aaa">                // namePrefix is set to `aaa`
 *
 *    <element />                   // element namePrefix will be `aaa`
 *
 *    <NamePrefixModifier appendNamePrefix="bbb">       // namePrefix has appended `bbb` making it `aaa.bbb`
 *
 *      <element />                 // element namePrefix will be `aaa.bbb`
 *
 *       <NamePrefixModifier namePrefix="zzz">          // namePrefix is explicitly set to `zzz`
 *
 *         <element4 />              // element namePrefix will be `zzz`
 *
 *       </NamePrefixModifier>
 *
 *    </NamePrefixModifier>
 *
 * </NamePrefixModifier>
 * ```
 *
 */
export const NamePrefixModifier: React.FC<React.PropsWithChildren<NamePrefixModifierProps>> = ({
  namePrefix,
  appendNamePrefix,
  children,
}) => (
  <InjectedPropsModifier
    propModifier={(consumerProps: NamePrefixConsumerProps) => {
      // Updated namePrefix with our new value
      if (namePrefix) {
        consumerProps.namePrefix = namePrefix;
      } else if (appendNamePrefix) {
        consumerProps.namePrefix = joinPaths(consumerProps.namePrefix || '', appendNamePrefix);
      }
      consumerProps.prefixName = (n) => joinPaths(consumerProps.namePrefix, n);
    }}
  >
    {children}
  </InjectedPropsModifier>
);
